/**
 * Ввод для canvas и связанные сервисы — один контракт:
 *
 * **Почему тач «ожил»:** наличие `window.PointerEvent` не означает, что касания по WebGL-canvas
 * приходят как pointer-события. На части WebView/браузеров тач остаётся только Touch Events.
 * Если подписаться только на `pointer*`, шина не получает событий → сломан выбор, pinch/pan.
 *
 * **`CANVAS_USES_POINTER_EVENTS === false` (по умолчанию):** на canvas — классическая связка
 * `mousedown`/`mousemove`/`mouseup` + `touchstart`/`touchmove`/`touchend`/`touchcancel`.
 * Жесты pinch/pan в `ZoomCameraService` / `MoveCameraService` используют Touch API на этом режиме.
 *
 * **`POINTER_EVENTS_SUPPORTED`:** флаг возможности Pointer Events в движке; уместен для
 * **document-level** слушателей (`KeyStateService`: capture `pointerdown`/`up`/`cancel`),
 * чтобы `isMouseHold` корректен при мультитаче, независимо от того, как подписан canvas.
 *
 * При включении `CANVAS_USES_POINTER_EVENTS` нужно снова отдавать pinch/pan через pointer-ветку
 * сервисов и держать в актуальном состоянии учёт активных тач-указателей на шине (см. EventBus).
 */
export const POINTER_EVENTS_SUPPORTED =
  typeof window !== 'undefined' && typeof (window as any).PointerEvent !== 'undefined';

/** См. блок выше — для стабильного тача по canvas оставляем mouse + Touch Events. */
export const CANVAS_USES_POINTER_EVENTS = false;

/** Минимальное относительное изменение расстояния между двумя пальцами, чтобы считать это pinch. */
export const PINCH_DISTANCE_EPSILON = 0.02;

/** Приводит clientX/Y к offsetX/Y относительно элемента (canvas). */
export function patchPointerOffsets(e: any, target: EventTarget): void {
  if (!e || !target || !(target as HTMLElement).getBoundingClientRect) {
    return;
  }
  const rect = (target as HTMLElement).getBoundingClientRect();
  e.offsetX = e.clientX - rect.left;
  e.offsetY = e.clientY - rect.top;
}

/**
 * Второй и следующие пальцы в multi-touch.
 * Если `isPrimary` не задан (boolean) — не отбрасываем событие: иначе Safari/WebView
 * могут сломать тап и выделение пола.
 */
export function isSecondaryTouchPointer(e: any): boolean {
  return (
    !!e &&
    e.pointerType === 'touch' &&
    typeof e.isPrimary === 'boolean' &&
    e.isPrimary === false
  );
}

/** Touch/pen всегда патчим; mouse — если offset не задан (некоторые WebView). */
export function normalizePointerEventForCanvas(e: any): void {
  if (!e) {
    return;
  }
  const el = (e.currentTarget || e.target) as HTMLElement | undefined;
  if (!el || typeof el.getBoundingClientRect !== 'function') {
    return;
  }
  const pt = e.pointerType;
  if (pt === 'touch' || pt === 'pen') {
    patchPointerOffsets(e, el);
    return;
  }
  if (typeof e.offsetX !== 'number' || typeof e.offsetY !== 'number') {
    patchPointerOffsets(e, el);
  }
}

export function hypotDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.hypot(x2 - x1, y2 - y1);
}

export function centroidFromPoints(pts: Array<{ x: number; y: number }>): { x: number; y: number } | null {
  if (!pts.length) {
    return null;
  }
  let x = 0;
  let y = 0;
  for (let i = 0; i < pts.length; i++) {
    x += pts[i].x;
    y += pts[i].y;
  }
  const n = pts.length;
  return { x: x / n, y: y / n };
}

export function twoTouchesSeparation(touches: TouchList): number | null {
  if (!touches || touches.length !== 2) {
    return null;
  }
  const t0 = touches[0];
  const t1 = touches[1];
  return hypotDistance(t0.clientX, t0.clientY, t1.clientX, t1.clientY);
}



// WEBPACK FOOTER //
// ./src/app/utils/pointer-input.utils.ts